import { useEffect, useState } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Button, NumberInputField } from '@/components/ui'
import { AutoCompleteField } from '@/components/ui/auto-complete'
import { useListarImobiliariasCorretores } from '@/features/proposta/hooks'
import type { TDetalhesPropostaSchema } from '@/features/proposta/schemas/detalhes-proposta-schema'
import { showToast } from '@/infra/sonner'

const configuracaoNumberInputPor = {
  percentual: {
    label: t('proposta:detalhes_proposta.imobiliaria.inputs.label_percentual'),
    formatOptions: { minimumFractionDigits: 3, maximumFractionDigits: 3 },
    field: 'percentualCorretagemImobiliaria' as const,
  },
  valor: {
    label: t('proposta:detalhes_proposta.imobiliaria.inputs.label_valor'),
    formatOptions: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    field: 'valorCorretagemImobiliaria' as const,
  },
}

type TCardImobiliariaCorretorProps = {
  index: number
  onExcluirParticipacao: VoidFunction
}

export const CardImobiliariaCorretor = ({ index, onExcluirParticipacao }: TCardImobiliariaCorretorProps) => {
  const { control, watch, trigger } = useFormContext<TDetalhesPropostaSchema>()
  const [query, setQuery] = useState('')
  const { data, refetch, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListarImobiliariasCorretores({ descricao: query, tamanhoDaPagina: 20 })

  const participacaoPath = `detalhesDaProposta.imobiliariasCorretores.participacoes.${index}` as const
  const imobiliariaCorretor = watch(`${participacaoPath}`)

  const { field, formatOptions, label } = configuracaoNumberInputPor[imobiliariaCorretor.corretagem.tipo]

  const valorOuPercentualPreenchido = watch(`${participacaoPath}.corretagem.${field}`)

  useEffect(() => {
    if (valorOuPercentualPreenchido === 0) {
      showToast({
        message: t('proposta:detalhes_proposta.imobiliaria.errors.toast.valor_zerado'),
        variant: 'error',
        closeButton: true,
      })
    }
  }, [valorOuPercentualPreenchido])

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-y-1">
      <AutoCompleteField
        valueKey="idExterno"
        control={control}
        name={`${participacaoPath}.imobiliariaCorretor`}
        data={data?.pages.flatMap((page) => page.records) ?? []}
        onSearch={setQuery}
        labelKey="descricao"
        label="Imobiliária"
        isLoading={isLoading}
        isError={isError}
        retry={refetch}
        isLoadingMore={isFetchingNextPage}
        onEndReach={hasNextPage ? fetchNextPage : undefined}
        className="w-full truncate md:w-50 xl:w-96"
        onSelect={() => {
          trigger(`${participacaoPath}`)
        }}
      />
      <NumberInputField
        className="md:max-w-50 xl:w-96"
        control={control}
        name={`${participacaoPath}.corretagem.${field}`}
        label={label}
        numberFieldProps={{ formatOptions }}
      />
      <Button type="button" variant="link" className="mb-3 h-10 items-end text-red-800" onClick={onExcluirParticipacao}>
        {t('proposta:detalhes_proposta.imobiliaria.botoes.excluir')}
      </Button>
    </div>
  )
}
